import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { apiClient } from '../lib/api';

interface TargetProfile {
  uid: string;
  profile: {
    name: string | null;
    email: string | null;
    photoUrl?: string | null;
  } | null;
  /**
   * `professor` é legado — preferir `profile.accountType === 'business'`.
   * Aqui só é usado `admin` (linha 45, gate anti-impersonate).
   */
  roles: { admin?: boolean; professor?: boolean };
  entitlements: { subscriptionStatus: string };
}

interface ImpersonationContextType {
  targetUid: string | null;
  targetProfile: TargetProfile | null;
  isImpersonating: boolean;
  startImpersonation: (uid: string) => Promise<void>;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

const STORAGE_KEY = 'impersonate-uid';
const PROFILE_KEY = 'impersonate-profile';

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  // Hydration fix: iniciar null em SSR e client (mesmo valor → zero mismatch).
  // useEffect lê sessionStorage após hidratação — banner de impersonação aparecerá
  // após hidratação, aceitável e melhor que invalidar todo o SSG.
  const [targetUid, setTargetUid] = useState<string | null>(null);
  const [targetProfile, setTargetProfile] = useState<TargetProfile | null>(null);

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    const uid = sessionStorage.getItem(STORAGE_KEY);
    const profileRaw = sessionStorage.getItem(PROFILE_KEY);
    if (uid) setTargetUid(uid);
    if (profileRaw) {
      try {
        setTargetProfile(JSON.parse(profileRaw));
      } catch {
        // JSON corrompido, ignora
      }
    }
  }, []);

  const startImpersonation = useCallback(async (uid: string) => {
    const res = await apiClient(`/admin/users/${uid}/profile`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Erro ao buscar perfil do usuário');
    }
    const profile: TargetProfile = await res.json();

    if (profile.roles?.admin) {
      throw new Error('Não é possível impersonar outro admin');
    }

    sessionStorage.setItem(STORAGE_KEY, uid);
    sessionStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    setTargetUid(uid);
    setTargetProfile(profile);

    // Recarregar a página para que todos os dados sejam refetchados com o novo contexto
    window.location.reload();
  }, []);

  const stopImpersonation = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(PROFILE_KEY);
    setTargetUid(null);
    setTargetProfile(null);

    // Recarregar para limpar todos os dados em cache
    window.location.reload();
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{
        targetUid,
        targetProfile,
        isImpersonating: targetUid !== null,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return ctx;
}
